// src/components/BankPosting/BankPosting.jsx
// ============================================================================
// BLOCK BP.PAGE.002 — Bank Posting feature shell
// ----------------------------------------------------------------------------
// PURPOSE
// - Owns the Bank Posting screen layout and panel wiring
// - Bridges left-tree selection, checkbox state, and right-panel actions
// - Keeps assigned child rows synchronized with selected transaction detail
// - Controls assign/remove/submit actions without moving API logic into the view
//
// IMPORTANT
// - Current controller contract is preserved
// - Until controller is expanded, child rows are only guaranteed for the
//   currently loaded transaction (selectedTransaction)
// ============================================================================

import React from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  fetchAutoMatchProgress,
  fetchCustomerAliases,
  removeCustomerAlias,
  updateCustomerAlias,
} from './api/bankPostingApi.js';
import BankPostingInvoiceList from './components/BankPostingInvoiceList.jsx';
import BankPostingPastPostingsList from './components/BankPostingPastPostingsList.jsx';
import BankPostingRemovedItems from './components/BankPostingRemovedItems.jsx';
import BankPostingSearchBox from './components/BankPostingSearchBox.jsx';
import BankPostingTransactionTree from './components/BankPostingTransactionTree.jsx';
import BankPostingUpdateAliasDialog from './components/BankPostingUpdateAliasDialog.jsx';
import useBankPostingController from './hooks/useBankPostingController.js';
import { matchesUnifiedSearch, normalizeDisplayInvoiceNumber } from './searchUtils.js';

// ============================================================================
// BLOCK BP.PAGE.002.01 — Invoice list filtering helper
// ----------------------------------------------------------------------------
// PURPOSE
// - Applies local search to suggested / open invoice lists
// ============================================================================
function filterInvoiceRows(rows, search) {
  const sourceRows = Array.isArray(rows) ? rows : [];

  return sourceRows.filter((row) =>
    matchesUnifiedSearch({
      search,
      textFields: [row?.invoice_number, row?.raw_invoice_number, row?.payment_number],
      amountFields: [],
      invoiceFields: [row?.invoice_number, row?.raw_invoice_number, row?.payment_number],
    })
  );
}

function resolveComparableScore(value) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  if (parsed > 0 && parsed < 1) {
    return parsed * 100;
  }

  return parsed;
}

function resolveBankTransactionColor(row) {
  const childRows = Array.isArray(row?.selected_invoices) ? row.selected_invoices : [];

  if (!childRows.length) {
    return 'blank';
  }

  const scores = childRows
    .map((invoice) =>
      resolveComparableScore(invoice?.score ?? invoice?.match_score ?? invoice?.confidence_score)
    )
    .filter((score) => Number.isFinite(score));

  if (!scores.length) {
    return 'blank';
  }

  const bestScore = Math.max(...scores);

  if (bestScore >= 80) {
    return 'green';
  }

  if (bestScore >= 40) {
    return 'yellow';
  }

  return 'blank';
}

function filterGroupedRows(groups, predicate) {
  const sourceGroups = Array.isArray(groups) ? groups : [];

  return sourceGroups
    .map((group) => {
      const rows = Array.isArray(group?.transactions) ? group.transactions : [];
      const filteredTransactions = rows.filter(predicate);

      return {
        ...group,
        transactions: filteredTransactions,
      };
    })
    .filter((group) => group.transactions.length > 0);
}

function childInvoiceKey(invoice) {
  return (
    invoice?.invoice_key ||
    `${invoice?.source || 'selected'}:${invoice?.invoice_number || ''}:${invoice?.masid || ''}`
  );
}

function normalizeTransactionKey(value) {
  return String(value ?? '').trim();
}

function parseScopedChildKey(value) {
  const normalized = String(value || '');
  const delimiterIndex = normalized.indexOf('::');

  if (delimiterIndex <= 0) {
    return null;
  }

  const transactionId = normalizeTransactionKey(normalized.slice(0, delimiterIndex));
  const childKey = normalized.slice(delimiterIndex + 2);

  if (!transactionId || !childKey) {
    return null;
  }

  return {
    transactionId,
    childKey,
  };
}

function buildAssignedDisplayKey(invoiceNumber) {
  return normalizeDisplayInvoiceNumber(invoiceNumber);
}

function getTransactionDescription(row) {
  const fullDescription = String(row?.full_description ?? '').trim();
  const shortDescription = String(row?.description ?? '').trim();
  const selected = fullDescription || shortDescription;

  return selected
    .replace(/preauthorized\s+ach\s+credit\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatTransactionDate(value) {
  if (!value) return '';

  const raw = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [yyyy, mm, dd] = raw.split('-');
    return `${mm}-${dd}-${yyyy}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  const yyyy = parsed.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

function buildAssignedTransactionSummary(row) {
  const dateLabel = formatTransactionDate(row?.assigned_transaction_date || row?.date);
  const amount = Number(
    row?.assigned_transaction_amount !== undefined && row?.assigned_transaction_amount !== null
      ? row?.assigned_transaction_amount
      : row?.amount
  );
  const amountLabel = Number.isFinite(amount) ? formatMoney(amount) : '';

  if (dateLabel && amountLabel) {
    return `${dateLabel} : ${amountLabel}`;
  }

  return dateLabel || amountLabel || getTransactionDescription(row);
}

function sanitizeAssignedTransactionDescription(value, disallowedValues = []) {
  const text = String(value ?? '').trim();

  if (!text) {
    return '';
  }

  const normalizedText = normalizeTransactionKey(text);
  const normalizedDisallowed = new Set(
    (disallowedValues || []).map((entry) => normalizeTransactionKey(entry)).filter(Boolean)
  );

  // Never display values that are known transaction ids for this row.
  if (normalizedText && normalizedDisallowed.has(normalizedText)) {
    return '';
  }

  // Hide opaque internal ids (for example Mongo-like 24-char hex ids) from UI labels.
  if (/^[a-f0-9]{24}$/i.test(text)) {
    return '';
  }

  // Hide common UUID-like ids.
  if (/^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(text)) {
    return '';
  }

  // Hide long opaque id-like tokens (letters/digits/underscore/hyphen only).
  if (/^[a-z0-9_-]{20,}$/i.test(text)) {
    return '';
  }

  return text;
}

function toMoneyNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
}

function formatMoney(value) {
  return `$${toMoneyNumber(value).toFixed(2)}`;
}

function onlyDigits(value) {
  return String(value ?? '').replace(/\D/g, '');
}

function deriveBankCodeFromAccountValue(value) {
  const lastFour = onlyDigits(value).slice(-4);

  if (lastFour === '3890') return 'A';
  if (lastFour === '3882') return 'B';

  return '';
}

function getTransactionAccountValue(row) {
  return String(row?.account_number || row?.account || '').trim();
}

const selectableTextFieldSx = {
  '& .MuiInputBase-input': {
    userSelect: 'text',
    WebkitUserSelect: 'text',
    MozUserSelect: 'text',
    cursor: 'text',
  },
  '& .MuiInputBase-root': {
    cursor: 'text',
  },
};

function getSelectedInvoiceAmount(row) {
  const invoiceAmount = Number(row?.invoice_amount ?? row?.amount ?? row?.invoice_balance);
  const payment = Number(
    row?.applied_amount_override ??
      row?.applied_amount ??
      row?.payment ??
      row?.cash_amount_applied ??
      0
  );

  // Business rule: parent transaction totals are based on cash/payment only.
  // Discount remains part of the child invoice balance math, but it does not
  // reduce the parent transaction amount used for zero-balance/submit gating.
  if (Number.isFinite(payment)) {
    return toMoneyNumber(payment);
  }

  if (Number.isFinite(invoiceAmount)) {
    return toMoneyNumber(invoiceAmount);
  }

  const fallbackBalance = Number(
    row?.invoice_balance ?? row?.balance_due ?? row?.open_balance ?? row?.amount_due
  );

  return Number.isFinite(fallbackBalance) ? toMoneyNumber(fallbackBalance) : 0;
}

function hasValidSubmitInvoiceNumber(invoice) {
  return Boolean(
    normalizeDisplayInvoiceNumber(
      invoice?.invoice_number || invoice?.raw_invoice_number || invoice?.masio_invoice_number
    )
  );
}

function isSubmittableAssignedInvoice(invoice) {
  return (
    hasValidSubmitInvoiceNumber(invoice) && Math.abs(getSelectedInvoiceAmount(invoice)) > 0.005
  );
}

function buildImportSummary(result) {
  if (!result) {
    return '';
  }

  return [
    `Rows: ${result.rowsReceived}`,
    `Inserted: ${result.insertedRows}`,
    `Duplicates: ${result.duplicateRows}`,
    `Dup Existing: ${result.duplicateExistingRows}`,
    `Dup In File: ${result.duplicateFileRows}`,
    `New Same-Date Dups: ${result.duplicateInFileNewDateRows || 0}`,
    `Skipped Time: ${result.skippedTimeRows}`,
    `Skipped: ${result.skippedRows}`,
    `Invalid: ${result.invalidRows}`,
  ].join(' | ');
}

function buildImportPostActionSummary(result) {
  const postActions = result?.postActions || null;

  if (!postActions) {
    return '';
  }

  const parts = [];

  if (postActions.rebuiltMatchSeeds) {
    const seedRows = postActions.rebuildSummary?.payment_seed_rows;
    parts.push(
      Number.isFinite(Number(seedRows)) ? `Seeds Rebuilt: ${Number(seedRows)}` : 'Seeds Rebuilt'
    );
  }

  if (postActions.ranAutoMatch) {
    const linkedCount = postActions.autoMatchSummary?.created_auto_links;
    parts.push(
      Number.isFinite(Number(linkedCount))
        ? `Auto-Match Links: ${Number(linkedCount)}`
        : 'Auto-Match Ran'
    );
  }

  return parts.join(' | ');
}

function buildAutoMatchSummary(summary) {
  const processed = Number(summary?.processed_transactions || 0);
  const matched = Number(summary?.matched_transactions || 0);
  const errored = Number(summary?.errored_transactions || 0);
  const created = Number(summary?.created_auto_links || 0);
  const createdOpen = Number(summary?.created_open_auto_links || 0);
  const remaining = Math.max(processed - matched - errored, 0);

  return [
    `Total items: ${processed}`,
    `Matched: ${matched}`,
    `Remaining: ${remaining}`,
    `Errors: ${errored}`,
    `Created links: ${created}`,
    `Open fallback links: ${createdOpen}`,
  ].join(' | ');
}

const AUTO_MATCH_PROGRESS_TICK_MS = 1200;
const AUTO_MATCH_MIN_ESTIMATED_DURATION_SECONDS = 1;
const AUTO_MATCH_MAX_ESTIMATED_DURATION_SECONDS = 200;

function estimateAutoMatchDurationSeconds(totalItems) {
  const normalizedTotal = Math.max(Number(totalItems) || 0, 0);

  return Math.max(
    AUTO_MATCH_MIN_ESTIMATED_DURATION_SECONDS,
    Math.min(
      Math.round(normalizedTotal * 3.9 + AUTO_MATCH_MIN_ESTIMATED_DURATION_SECONDS),
      AUTO_MATCH_MAX_ESTIMATED_DURATION_SECONDS
    )
  );
}

export default function BankPosting() {
  const controller = useBankPostingController();

  // ============================================================================
  // BLOCK BP.PAGE.002.02 — Controller state and action bindings
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Pulls page state and controller actions into the Bank Posting shell
  // - Includes both invoice assignment and invoice removal flows
  // ============================================================================
  const {
    loading,
    submitting,
    detailLoading,
    error,
    filters,
    transactionGroups,
    removedGroups,
    selectedTransaction,
    selectedDetails,
    pastPostingRows = [],
    pastPostingLoading = false,
    selectedPastPosting,
    selectedRemovedStateIds,
    submitForm,
    csvImporting,
    csvImportResult,
    setFilterValue,
    setSubmitValue,
    clearCsvImportResult,
    handleSelectPastPosting,
    loadLeftPanels,
    loadTransactionDetails,
    handleRunAutoMatch,
    handleStartOver,
    handleRebuildMatchSeeds,
    handleRestoreSubmittedState,
    handleAssignInvoices,
    handleRemoveInvoices,
    handleMarkRemoved,
    handleSubmitSelected,
    handleReversePastPosting,
    handleRestoreRemoved,
    handleUpdateRemovedClearFlag,
    handleToggleAllRemovedClearFlags,
    handleClearRemovedItems,
    handleImportCsvDescriptions,
    handleUpdateAssignedInvoice,
    handleChangeSuggestionPage,
    handleChangeOpenPage,
  } = controller;

  const csvFileInputRef = React.useRef(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = React.useState(false);
  const [selectedImportFile, setSelectedImportFile] = React.useState(null);
  const [importRebuildSeeds, setImportRebuildSeeds] = React.useState(false);
  const [importRunAutoMatch, setImportRunAutoMatch] = React.useState(false);
  const [autoMatchProgressOpen, setAutoMatchProgressOpen] = React.useState(false);
  const [autoMatchProgressTotal, setAutoMatchProgressTotal] = React.useState(0);
  const [autoMatchProgressRemaining, setAutoMatchProgressRemaining] = React.useState(0);
  const [autoMatchElapsedSeconds, setAutoMatchElapsedSeconds] = React.useState(0);
  const [autoMatchEstimatedDurationSeconds, setAutoMatchEstimatedDurationSeconds] = React.useState(
    AUTO_MATCH_MIN_ESTIMATED_DURATION_SECONDS
  );
  const [isReverseConfirmOpen, setIsReverseConfirmOpen] = React.useState(false);
  const [isUpdateAliasDialogOpen, setIsUpdateAliasDialogOpen] = React.useState(false);
  const [isAliasSubmitting, setIsAliasSubmitting] = React.useState(false);
  const [importToast, setImportToast] = React.useState({
    open: false,
    severity: 'success',
    message: '',
  });

  // ============================================================================
  // BLOCK BP.PAGE.002.03 — Checkbox state by panel and tree
  // ----------------------------------------------------------------------------
  // checkedSuggested:
  //   selected invoice keys from Suggested Invoices panel
  // checkedOpen:
  //   selected invoice keys from Open Invoices panel
  // checkedTransactionIds:
  //   selected parent bank transaction rows in left tree
  // checkedChildKeys:
  //   selected assigned child invoice rows under a bank transaction
  // ============================================================================
  const [checkedSuggested, setCheckedSuggested] = React.useState([]);
  const [checkedOpen, setCheckedOpen] = React.useState([]);
  const [checkedTransactionIds, setCheckedTransactionIds] = React.useState([]);
  const [checkedChildKeys, setCheckedChildKeys] = React.useState([]);
  const [zeroBalanceAutoSelectEnabled, setZeroBalanceAutoSelectEnabled] = React.useState(false);

  const toggleKey = React.useCallback(
    (setter) => (key) => {
      setter((prev) => (prev.includes(key) ? prev.filter((row) => row !== key) : [...prev, key]));
    },
    []
  );

  const handleToggleTransaction = React.useCallback((row) => {
    setCheckedTransactionIds((prev) =>
      prev.includes(normalizeTransactionKey(row.id))
        ? prev.filter((id) => id !== normalizeTransactionKey(row.id))
        : [...prev, normalizeTransactionKey(row.id)]
    );
  }, []);

  const handleSelectTransaction = React.useCallback(
    async (row) => {
      if (!normalizeTransactionKey(row?.id)) return;

      if (normalizeTransactionKey(selectedTransaction?.id) !== normalizeTransactionKey(row.id)) {
        // Clicking a tree row should load details without creating/removing links.
        await loadTransactionDetails(row, {}, { autoLink: false });
      }
    },
    [loadTransactionDetails, selectedTransaction?.id]
  );

  const handleToggleChildInvoice = React.useCallback((invoice, row) => {
    const key = `${normalizeTransactionKey(row.id)}::${childInvoiceKey(invoice)}`;

    setCheckedChildKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  }, []);

  React.useEffect(() => {
    setCheckedSuggested([]);
    setCheckedOpen([]);
    setCheckedChildKeys([]);
  }, [selectedTransaction?.id]);

  // ============================================================================
  // BLOCK BP.PAGE.002.04 — Filtered right-panel invoice lists
  // ============================================================================
  const visibleSuggested = React.useMemo(
    () => filterInvoiceRows(selectedDetails.suggestedInvoices, filters.suggestedSearch),
    [filters.suggestedSearch, selectedDetails.suggestedInvoices]
  );

  const visibleOpen = React.useMemo(
    () => (Array.isArray(selectedDetails.openInvoices) ? selectedDetails.openInvoices : []),
    [selectedDetails.openInvoices]
  );

  // ============================================================================
  // BLOCK BP.PAGE.002.05 — Assigned invoice maps by transaction
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Uses transactionGroups payload as the baseline source of child rows for
  //   all parent transactions in the left tree
  // - Builds global lookups so Suggested/Open rows can show assigned state even
  //   when invoice_key differs between SAGE/Open and LOKA-selected rows
  // ============================================================================
  const assignedInvoiceMaps = React.useMemo(() => {
    const invoicesByTransactionId = {};
    const invoiceOwnerByKey = {};
    const invoiceOwnerByDisplayKey = {};

    transactionGroups.forEach((group) => {
      (group.transactions || []).forEach((row) => {
        const childRows = Array.isArray(row.selected_invoices) ? row.selected_invoices : [];

        if (childRows.length) {
          invoicesByTransactionId[normalizeTransactionKey(row.id)] = childRows;
        }

        childRows.forEach((invoice) => {
          const sourceInvoiceKey = invoice?.source_invoice_key;

          if (sourceInvoiceKey) {
            invoiceOwnerByKey[sourceInvoiceKey] = row;
          }

          if (invoice?.invoice_key) {
            invoiceOwnerByKey[invoice.invoice_key] = row;
          }

          const displayKey = buildAssignedDisplayKey(invoice?.invoice_number);
          if (displayKey) {
            invoiceOwnerByDisplayKey[displayKey] = row;
          }
        });
      });
    });

    // Keep left-tree assignment state anchored to transactionGroups payload.
    // The suggestion/details endpoint can briefly return transient rows during
    // selection, which caused row resorting and child-row disappearance.

    return {
      assignedInvoicesByTransactionId: invoicesByTransactionId,
      assignedInvoiceOwnerByKey: invoiceOwnerByKey,
      assignedInvoiceOwnerByDisplayKey: invoiceOwnerByDisplayKey,
    };
  }, [transactionGroups]);

  const assignedInvoicesByTransactionId = assignedInvoiceMaps.assignedInvoicesByTransactionId;
  const assignedInvoiceOwnerByKey = assignedInvoiceMaps.assignedInvoiceOwnerByKey;
  const assignedInvoiceOwnerByDisplayKey = assignedInvoiceMaps.assignedInvoiceOwnerByDisplayKey;

  const filteredTransactionGroups = React.useMemo(() => {
    const rawSearch = String(filters.search ?? '').trim();
    const colorTokens = ['green', 'yellow', 'blank'];
    const colorTerms = rawSearch
      .split(/[\s,]+/)
      .map((token) => token.trim().toLowerCase())
      .filter((token) => colorTokens.includes(token));

    const remainingSearch = rawSearch
      .split(/[\s,]+/)
      .map((token) => token.trim())
      .filter((token) => token && !colorTokens.includes(token.toLowerCase()))
      .join(' ');

    return filterGroupedRows(transactionGroups, (row) => {
      const childRows =
        assignedInvoicesByTransactionId[normalizeTransactionKey(row?.id)] ||
        row?.selected_invoices ||
        [];
      const invoiceFields = childRows.flatMap((invoice) => [
        invoice?.invoice_number,
        invoice?.raw_invoice_number,
      ]);

      const colorMatch =
        colorTerms.length === 0 ||
        colorTerms.some((term) => resolveBankTransactionColor(row) === term);

      const searchMatch = matchesUnifiedSearch({
        search: remainingSearch,
        textFields: [
          row?.full_description,
          row?.description,
          row?.transaction_id,
          row?.check_number,
          row?.date,
        ],
        amountFields: [row?.amount, row?.balance],
        invoiceFields,
      });

      return colorMatch && searchMatch;
    });
  }, [assignedInvoicesByTransactionId, filters.search, transactionGroups]);

  const filteredRemovedGroups = React.useMemo(
    () =>
      filterGroupedRows(removedGroups, (row) =>
        matchesUnifiedSearch({
          search: filters.removedSearch,
          textFields: [row?.full_description, row?.description, row?.date],
          amountFields: [row?.amount],
          invoiceFields: [],
        })
      ),
    [filters.removedSearch, removedGroups]
  );

  // ============================================================================
  // BLOCK BP.PAGE.002.06 — Assigned-state decoration for right-panel rows
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Prevents already-assigned rows from looking safely assignable
  // - Uses both invoice_key and normalized displayed invoice number so assigned
  //   rows can still be detected when SAGE/Open and LOKA use different keys
  // ============================================================================
  const displaySuggested = React.useMemo(
    () =>
      (visibleSuggested || []).map((row) => {
        const currentTransactionId = normalizeTransactionKey(selectedTransaction?.id);
        const displayKey = buildAssignedDisplayKey(row?.invoice_number);
        const persistedAssignmentId = normalizeTransactionKey(
          row?.assigned_transaction_id ||
            row?.linked_bank_transaction_id ||
            row?.manual_assigned_transaction_id
        );
        const owner =
          assignedInvoiceOwnerByKey[row.invoice_key] ||
          assignedInvoiceOwnerByDisplayKey[displayKey] ||
          null;
        const ownerTransactionId = normalizeTransactionKey(owner?.id);
        const assignedTransactionId = ownerTransactionId || persistedAssignmentId || null;
        const isAssignedElsewhere =
          Boolean(assignedTransactionId) && assignedTransactionId !== currentTransactionId;

        return {
          ...row,
          is_assigned_elsewhere: isAssignedElsewhere,
          assigned_transaction_id: isAssignedElsewhere ? assignedTransactionId : null,
          assigned_transaction_description: isAssignedElsewhere
            ? buildAssignedTransactionSummary(owner || row) ||
              sanitizeAssignedTransactionDescription(row?.assigned_transaction_description, [
                row?.assigned_transaction_id,
                row?.linked_bank_transaction_id,
                row?.manual_assigned_transaction_id,
              ])
            : '',
        };
      }),
    [
      assignedInvoiceOwnerByDisplayKey,
      assignedInvoiceOwnerByKey,
      selectedTransaction?.id,
      visibleSuggested,
    ]
  );

  const displayOpen = React.useMemo(
    () =>
      (visibleOpen || []).map((row) => {
        const currentTransactionId = normalizeTransactionKey(selectedTransaction?.id);
        const displayKey = buildAssignedDisplayKey(row?.invoice_number);
        const persistedAssignmentId = normalizeTransactionKey(
          row?.assigned_transaction_id ||
            row?.linked_bank_transaction_id ||
            row?.manual_assigned_transaction_id
        );
        const owner =
          assignedInvoiceOwnerByKey[row.invoice_key] ||
          assignedInvoiceOwnerByDisplayKey[displayKey] ||
          null;
        const ownerTransactionId = normalizeTransactionKey(owner?.id);
        const assignedTransactionId = ownerTransactionId || persistedAssignmentId || null;
        const hasAssignedElsewhereId =
          Boolean(assignedTransactionId) && assignedTransactionId !== currentTransactionId;
        const isGloballyLinkedElsewhere =
          Boolean(row?.is_globally_linked) && !assignedTransactionId;

        return {
          ...row,
          is_assigned_elsewhere: hasAssignedElsewhereId || isGloballyLinkedElsewhere,
          assigned_transaction_id: hasAssignedElsewhereId ? assignedTransactionId : null,
          assigned_transaction_description: hasAssignedElsewhereId
            ? buildAssignedTransactionSummary(owner || row) ||
              sanitizeAssignedTransactionDescription(row?.assigned_transaction_description, [
                row?.assigned_transaction_id,
                row?.linked_bank_transaction_id,
                row?.manual_assigned_transaction_id,
              ])
            : '',
        };
      }),
    [
      assignedInvoiceOwnerByDisplayKey,
      assignedInvoiceOwnerByKey,
      selectedTransaction?.id,
      visibleOpen,
    ]
  );

  const flatTransactions = React.useMemo(
    () => filteredTransactionGroups.flatMap((group) => group.transactions || []),
    [filteredTransactionGroups]
  );

  const allTransactions = React.useMemo(
    () => transactionGroups.flatMap((group) => group.transactions || []),
    [transactionGroups]
  );

  const allTransactionMap = React.useMemo(
    () => new Map(allTransactions.map((row) => [normalizeTransactionKey(row.id), row])),
    [allTransactions]
  );

  const flatTransactionMap = React.useMemo(
    () => new Map(flatTransactions.map((row) => [normalizeTransactionKey(row.id), row])),
    [flatTransactions]
  );

  React.useEffect(() => {
    setCheckedTransactionIds((prev) => {
      const next = prev.filter((id) => flatTransactionMap.has(normalizeTransactionKey(id)));
      return next.length === prev.length ? prev : next;
    });
  }, [flatTransactionMap]);

  const checkedTransactions = React.useMemo(
    () =>
      checkedTransactionIds
        .map((id) => flatTransactionMap.get(normalizeTransactionKey(id)))
        .filter(Boolean),
    [checkedTransactionIds, flatTransactionMap]
  );

  const checkedDerivedBankCodes = React.useMemo(
    () => [
      ...new Set(
        checkedTransactions
          .map((row) => deriveBankCodeFromAccountValue(getTransactionAccountValue(row)))
          .filter(Boolean)
      ),
    ],
    [checkedTransactions]
  );

  const handleAssignedElsewhereInvoiceClick = React.useCallback(
    async (row) => {
      const targetTransactionId = normalizeTransactionKey(
        row?.assigned_transaction_id ||
          row?.linked_bank_transaction_id ||
          row?.manual_assigned_transaction_id
      );

      if (!targetTransactionId) {
        return;
      }

      let targetTransaction = allTransactionMap.get(targetTransactionId) || null;

      if (!targetTransaction) {
        const refreshed = await loadLeftPanels({ preAutoLink: false });
        const refreshedTransactions = Array.isArray(refreshed?.groups)
          ? refreshed.groups.flatMap((group) => group?.transactions || [])
          : [];

        targetTransaction =
          refreshedTransactions.find(
            (candidate) => normalizeTransactionKey(candidate?.id) === targetTransactionId
          ) || null;
      }

      // If the owner transaction is not visible in the current queue, keep
      // the current selection instead of switching to an id-only placeholder.
      if (!targetTransaction) {
        return;
      }

      if (filters.search) {
        setFilterValue('search', '');
      }

      await handleSelectTransaction(targetTransaction);
    },
    [allTransactionMap, filters.search, handleSelectTransaction, loadLeftPanels, setFilterValue]
  );

  const suggestedSelectedTotal = React.useMemo(
    () =>
      displaySuggested
        .filter((row) => checkedSuggested.includes(row.invoice_key))
        .reduce((sum, row) => sum + getSelectedInvoiceAmount(row), 0),
    [checkedSuggested, displaySuggested]
  );

  const openSelectedTotal = React.useMemo(
    () =>
      displayOpen
        .filter((row) => checkedOpen.includes(row.invoice_key))
        .reduce((sum, row) => sum + getSelectedInvoiceAmount(row), 0),
    [checkedOpen, displayOpen]
  );

  const selectedTransactionBalance = toMoneyNumber(selectedTransaction?.balance ?? 0);
  const totalSelectedToPost = toMoneyNumber(suggestedSelectedTotal + openSelectedTotal);
  const remainingToZero = toMoneyNumber(selectedTransactionBalance - totalSelectedToPost);

  const remainingToZeroLabel = selectedTransaction
    ? `Remaining to $0.00: ${formatMoney(remainingToZero)}`
    : 'Remaining to $0.00: Select a bank transaction';

  const hasPastPostingSearch = Boolean(String(filters?.pastPostingSearch || '').trim());
  const hasSelectedPastPosting = Boolean(selectedPastPosting?.key);
  const isSelectedPastPostingReversed = Boolean(selectedPastPosting?.isReversed);

  const openReverseConfirmDialog = React.useCallback(() => {
    if (!hasSelectedPastPosting || submitting) {
      return;
    }

    setIsReverseConfirmOpen(true);
  }, [hasSelectedPastPosting, submitting]);

  const closeReverseConfirmDialog = React.useCallback(() => {
    if (!submitting) {
      setIsReverseConfirmOpen(false);
    }
  }, [submitting]);

  const confirmReversePastPosting = React.useCallback(async () => {
    try {
      await handleReversePastPosting();
      await loadLeftPanels({ preAutoLink: false });
    } catch (_error) {
      // Keep dialog open so user can review selected details after a failed attempt.
    }
  }, [handleReversePastPosting, loadLeftPanels]);

  const hasCheckedTransactions = checkedTransactions.length > 0;

  const getEffectiveBalance = React.useCallback(
    (row) => {
      const amount = Number(row?.amount);
      const assignedRows =
        assignedInvoicesByTransactionId[row?.id] ||
        (Array.isArray(row?.selected_invoices) ? row.selected_invoices : []);

      if (Number.isFinite(amount) && assignedRows.length) {
        const appliedTotal = assignedRows.reduce(
          (sum, invoice) => sum + getSelectedInvoiceAmount(invoice),
          0
        );

        return toMoneyNumber(amount - appliedTotal);
      }

      const fallbackBalance = Number(row?.balance ?? row?.amount ?? 0);
      return Number.isFinite(fallbackBalance) ? toMoneyNumber(fallbackBalance) : 0;
    },
    [assignedInvoicesByTransactionId]
  );

  const isZeroBalance = React.useCallback(
    (row) => {
      const assignedRows =
        assignedInvoicesByTransactionId[row?.id] ||
        (Array.isArray(row?.selected_invoices) ? row.selected_invoices : []);

      if (
        !assignedRows.length ||
        !assignedRows.every((invoice) => isSubmittableAssignedInvoice(invoice))
      ) {
        return false;
      }

      return Math.abs(getEffectiveBalance(row)) < 0.005;
    },
    [assignedInvoicesByTransactionId, getEffectiveBalance]
  );

  const visibleZeroBalanceTransactionIds = React.useMemo(
    () =>
      flatTransactions
        .filter((row) => isZeroBalance(row))
        .map((row) => normalizeTransactionKey(row?.id))
        .filter(Boolean),
    [flatTransactions, isZeroBalance]
  );

  const visibleZeroBalanceCount = visibleZeroBalanceTransactionIds.length;

  const handleToggleZeroBalanceAutoSelect = React.useCallback(
    (event) => {
      const isChecked = event.target.checked;
      setZeroBalanceAutoSelectEnabled(isChecked);

      setCheckedTransactionIds((prev) => {
        if (!isChecked) {
          const visibleZeroSet = new Set(visibleZeroBalanceTransactionIds);
          const next = prev.filter((id) => !visibleZeroSet.has(normalizeTransactionKey(id)));
          return next.length === prev.length ? prev : next;
        }

        if (
          prev.length === visibleZeroBalanceTransactionIds.length &&
          prev.every(
            (id, index) => normalizeTransactionKey(id) === visibleZeroBalanceTransactionIds[index]
          )
        ) {
          return prev;
        }

        return visibleZeroBalanceTransactionIds;
      });
    },
    [visibleZeroBalanceTransactionIds]
  );

  React.useEffect(() => {
    if (!zeroBalanceAutoSelectEnabled) {
      return;
    }

    setCheckedTransactionIds((prev) => {
      if (
        prev.length === visibleZeroBalanceTransactionIds.length &&
        prev.every(
          (id, index) => normalizeTransactionKey(id) === visibleZeroBalanceTransactionIds[index]
        )
      ) {
        return prev;
      }

      return visibleZeroBalanceTransactionIds;
    });
  }, [visibleZeroBalanceTransactionIds, zeroBalanceAutoSelectEnabled]);

  const openImportDialog = React.useCallback(() => {
    setSelectedImportFile(null);
    setImportRebuildSeeds(false);
    setImportRunAutoMatch(false);
    setIsImportDialogOpen(true);
  }, []);

  const closeImportDialog = React.useCallback(() => {
    if (!csvImporting) {
      setIsImportDialogOpen(false);
    }
  }, [csvImporting]);

  const triggerFilePicker = React.useCallback(() => {
    if (csvFileInputRef.current) {
      csvFileInputRef.current.click();
    }
  }, []);

  const handleSelectedImportFile = React.useCallback((event) => {
    const file = event?.target?.files?.[0] || null;
    setSelectedImportFile(file);
  }, []);

  const handleRunSeedRebuild = React.useCallback(async () => {
    try {
      const result = await handleRebuildMatchSeeds();
      const seedRows = result?.summary?.payment_seed_rows;
      setImportToast({
        open: true,
        severity: 'success',
        message: Number.isFinite(Number(seedRows))
          ? `Match seeds rebuilt. Rows: ${Number(seedRows)}`
          : 'Match seeds rebuilt.',
      });
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to rebuild match seeds.';
      setImportToast({
        open: true,
        severity: 'error',
        message,
      });
    }
  }, [handleRebuildMatchSeeds]);

  const handleRefreshTransactionList = React.useCallback(async () => {
    try {
      await loadLeftPanels({ preAutoLink: false });
      setImportToast({
        open: true,
        severity: 'success',
        message: 'Bank transaction list refreshed.',
      });
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to refresh list.';
      setImportToast({ open: true, severity: 'error', message });
    }
  }, [loadLeftPanels]);

  const handleRestoreCleared = React.useCallback(async () => {
    try {
      const result = await handleRestoreSubmittedState();
      const count = result?.restored_count ?? 0;
      setImportToast({
        open: true,
        severity: count > 0 ? 'success' : 'info',
        message:
          count > 0
            ? `Restored ${count} transaction(s) to cleared state.`
            : 'No transactions needed state recovery.',
      });
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to restore cleared state.';
      setImportToast({ open: true, severity: 'error', message });
    }
  }, [handleRestoreSubmittedState]);

  const handleRunAutoMatchWithProgress = React.useCallback(async () => {
    const visibleTransactionIds = flatTransactions
      .map((row) => normalizeTransactionKey(row?.id))
      .filter(Boolean);
    const totalItems = visibleTransactionIds.length;
    const estimatedDurationSeconds = estimateAutoMatchDurationSeconds(totalItems);

    setAutoMatchProgressTotal(totalItems);
    setAutoMatchProgressRemaining(totalItems);
    setAutoMatchElapsedSeconds(0);
    setAutoMatchEstimatedDurationSeconds(estimatedDurationSeconds);
    setAutoMatchProgressOpen(true);

    try {
      const result = await handleRunAutoMatch({
        transactionIds: visibleTransactionIds,
      });
      const summary = result?.summary || {};

      setAutoMatchProgressRemaining(0);

      setImportToast({
        open: true,
        severity: 'success',
        message: buildAutoMatchSummary(summary),
      });
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to run auto-match.';
      setImportToast({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setAutoMatchProgressRemaining(0);
      setAutoMatchProgressOpen(false);
    }
  }, [flatTransactions, handleRunAutoMatch]);

  const handleStartOverWithProgress = React.useCallback(async () => {
    const visibleTransactionIds = flatTransactions
      .map((row) => normalizeTransactionKey(row?.id))
      .filter(Boolean);
    const totalItems = visibleTransactionIds.length;
    const estimatedDurationSeconds = estimateAutoMatchDurationSeconds(totalItems);

    setAutoMatchProgressTotal(totalItems);
    setAutoMatchProgressRemaining(totalItems);
    setAutoMatchElapsedSeconds(0);
    setAutoMatchEstimatedDurationSeconds(estimatedDurationSeconds);
    setAutoMatchProgressOpen(true);

    try {
      const result = await handleStartOver({
        transactionIds: visibleTransactionIds,
      });
      setAutoMatchProgressRemaining(0);
      setImportToast({
        open: true,
        severity: 'success',
        message: buildAutoMatchSummary(result?.summary || {}),
      });
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to start over.';
      setImportToast({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setAutoMatchProgressOpen(false);
    }
  }, [flatTransactions, handleStartOver]);

  React.useEffect(() => {
    if (!autoMatchProgressOpen) {
      return undefined;
    }

    const timerId = window.setInterval(async () => {
      setAutoMatchElapsedSeconds((prev) => Math.round(prev + AUTO_MATCH_PROGRESS_TICK_MS / 1000));
      try {
        const progress = await fetchAutoMatchProgress();
        if (progress.running && progress.total > 0) {
          setAutoMatchProgressRemaining(Math.max(progress.total - progress.processed, 0));
        }
      } catch {
        // Fall back to time-based decrement if the poll fails
        setAutoMatchProgressRemaining((currentRemaining) => Math.max(currentRemaining - 1, 0));
      }
    }, AUTO_MATCH_PROGRESS_TICK_MS);

    return () => {
      window.clearInterval(timerId);
    };
  }, [autoMatchProgressOpen]);

  const handleRunCsvImport = React.useCallback(async () => {
    if (!selectedImportFile) {
      setImportToast({
        open: true,
        severity: 'warning',
        message: 'Select a CSV file before submitting.',
      });
      return;
    }

    const shouldShowAutoMatchProgress = Boolean(importRunAutoMatch);

    if (shouldShowAutoMatchProgress) {
      const visibleTransactionIds = flatTransactions
        .map((row) => normalizeTransactionKey(row?.id))
        .filter(Boolean);
      const totalItems = visibleTransactionIds.length;
      const estimatedDurationSeconds = estimateAutoMatchDurationSeconds(totalItems);

      setAutoMatchProgressTotal(totalItems);
      setAutoMatchProgressRemaining(totalItems);
      setAutoMatchElapsedSeconds(0);
      setAutoMatchEstimatedDurationSeconds(estimatedDurationSeconds);
      setAutoMatchProgressOpen(true);
    }

    try {
      const result = await handleImportCsvDescriptions(selectedImportFile, {
        rebuildMatchSeeds: importRebuildSeeds,
        runAutoMatch: importRunAutoMatch,
      });
      const summary = [buildImportSummary(result), buildImportPostActionSummary(result)]
        .filter(Boolean)
        .join(' | ');
      const hasNewSameDateDuplicates = Number(result?.duplicateInFileNewDateRows || 0) > 0;
      const importMessage = hasNewSameDateDuplicates
        ? `${summary} | New transaction duplicate(s) detected for the same date and imported.`
        : summary;

      setImportToast({
        open: true,
        severity: hasNewSameDateDuplicates ? 'warning' : 'success',
        message: importMessage,
      });
      setIsImportDialogOpen(false);
      setSelectedImportFile(null);
      setImportRebuildSeeds(false);
      setImportRunAutoMatch(false);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'CSV import failed. Check file format and retry.';
      setImportToast({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      if (shouldShowAutoMatchProgress) {
        setAutoMatchProgressRemaining(0);
        setAutoMatchProgressOpen(false);
      }
    }
  }, [
    flatTransactions,
    handleImportCsvDescriptions,
    importRebuildSeeds,
    importRunAutoMatch,
    selectedImportFile,
  ]);

  const handleCloseImportToast = React.useCallback(() => {
    setImportToast((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const allCheckedRowsZeroBalance =
    hasCheckedTransactions && checkedTransactions.every((row) => isZeroBalance(row));

  const nonSubmittableCheckedCount = checkedTransactions.filter(
    (row) => !isZeroBalance(row)
  ).length;
  const submitButtonDisabled = !allCheckedRowsZeroBalance;

  // ============================================================================
  // BLOCK BP.PAGE.002.07 — Assign and remove actions
  // ----------------------------------------------------------------------------
  // BEHAVIOR
  // - Suggested/Open -> selected bank transaction uses assign flow
  // - Selected child invoices under bank transaction -> back out of selected
  //   bank transaction uses remove flow
  // ============================================================================
  const moveSuggestedToSelected = async () => {
    const rows = displaySuggested.filter(
      (row) => checkedSuggested.includes(row.invoice_key) && !row.is_assigned_elsewhere
    );

    if (!rows.length) {
      return;
    }

    await handleAssignInvoices(
      rows.map((row) => ({
        ...row,
        invoice_source: row.invoice_source || row.source,
        match_origin:
          row.has_loka_invoice_match === false
            ? 'open-manual-placement'
            : row.match_origin || 'suggested',
        allow_manual_open_assignment:
          row.allow_manual_open_assignment === true || row.has_loka_invoice_match === false,
      }))
    );

    setCheckedSuggested([]);
  };
  const moveOpenToSelected = async () => {
    const rows = displayOpen.filter(
      (row) => checkedOpen.includes(row.invoice_key) && !row.is_assigned_elsewhere
    );

    if (!rows.length) {
      return;
    }

    await handleAssignInvoices(
      rows.map((row) => ({
        ...row,
        invoice_source: row.invoice_source || row.source,
        match_origin: row.has_loka_invoice_match === false ? 'open-manual-placement' : 'open',
        allow_manual_open_assignment: row.has_loka_invoice_match === false,
      }))
    );

    setCheckedOpen([]);
  };

  const moveSelectedBackToInvoicePool = async () => {
    if (!checkedChildKeys.length) return;

    const childKeysByTransactionId = new Map();

    checkedChildKeys.forEach((scopedKey) => {
      const parsed = parseScopedChildKey(scopedKey);
      if (!parsed) {
        return;
      }

      if (!childKeysByTransactionId.has(parsed.transactionId)) {
        childKeysByTransactionId.set(parsed.transactionId, new Set());
      }

      childKeysByTransactionId.get(parsed.transactionId).add(parsed.childKey);
    });

    if (!childKeysByTransactionId.size) return;

    for (const [transactionId, childKeySet] of childKeysByTransactionId.entries()) {
      const fallbackRows = Array.isArray(
        allTransactionMap.get(normalizeTransactionKey(transactionId))?.selected_invoices
      )
        ? allTransactionMap.get(normalizeTransactionKey(transactionId)).selected_invoices
        : [];
      const assignedRows = assignedInvoicesByTransactionId[transactionId] || fallbackRows;

      const checkedAssignedRows = assignedRows.filter((invoice) =>
        childKeySet.has(childInvoiceKey(invoice))
      );

      const selectedInvoiceKeys = [
        ...new Set(
          checkedAssignedRows
            .map((invoice) => {
              if (invoice?.is_manual_open_assignment) {
                const sourceKey = String(invoice?.source_invoice_key || '').trim();
                return sourceKey ? `manual:${sourceKey}` : '';
              }

              return invoice?.invoice_key || '';
            })
            .filter(Boolean)
        ),
      ];

      if (!selectedInvoiceKeys.length) {
        continue;
      }

      await handleRemoveInvoices(selectedInvoiceKeys, { transactionId });
    }

    setCheckedChildKeys([]);
  };

  // ============================================================================
  // BLOCK BP.PAGE.002.08 — Left-panel action rules
  // ----------------------------------------------------------------------------
  // RULES
  // - Row click selects the placement target for Suggested/Open assignment
  // - Left checkboxes are reserved for Remove Selected and Submit Selected
  // - Submit Selected is enabled only when every checked row has $0.00 balance
  // ============================================================================
  const placementTargetLabel = selectedTransaction
    ? `${getTransactionDescription(selectedTransaction) || '(no description)'} | Balance ${Number(
        selectedTransaction.balance || 0
      ).toFixed(2)}`
    : 'None selected';

  const aliasCustomerCandidate = React.useMemo(() => {
    const checkedSuggestedRows = displaySuggested.filter((row) =>
      checkedSuggested.includes(row?.invoice_key)
    );
    if (checkedSuggestedRows.length > 0) {
      return checkedSuggestedRows[0];
    }

    const checkedOpenRows = displayOpen.filter((row) => checkedOpen.includes(row?.invoice_key));
    if (checkedOpenRows.length > 0) {
      return checkedOpenRows[0];
    }

    const sourceSuggested = Array.isArray(selectedDetails?.suggestedInvoices)
      ? selectedDetails.suggestedInvoices
      : [];
    if (sourceSuggested.length > 0) {
      return sourceSuggested[0];
    }

    const sourceOpen = Array.isArray(selectedDetails?.openInvoices)
      ? selectedDetails.openInvoices
      : [];
    if (sourceOpen.length > 0) {
      return sourceOpen[0];
    }

    const sourceSelected = Array.isArray(selectedDetails?.selectedInvoices)
      ? selectedDetails.selectedInvoices
      : [];
    if (sourceSelected.length > 0) {
      return sourceSelected[0];
    }

    return null;
  }, [
    checkedOpen,
    checkedSuggested,
    displayOpen,
    displaySuggested,
    selectedDetails?.openInvoices,
    selectedDetails?.selectedInvoices,
    selectedDetails?.suggestedInvoices,
  ]);

  const updateAliasDefaults = React.useMemo(() => {
    const aliasText = getTransactionDescription(selectedTransaction);
    const customerNo =
      String(
        aliasCustomerCandidate?.masid ||
          aliasCustomerCandidate?.mas_customer_id ||
          aliasCustomerCandidate?.customer_no ||
          ''
      ).trim() || '';
    const customerName =
      String(
        aliasCustomerCandidate?.customer_name || aliasCustomerCandidate?.bill_to_name || ''
      ).trim() || '';

    return {
      aliasText,
      customerNo,
      arDivisionNo: '00',
      customerName,
    };
  }, [aliasCustomerCandidate, selectedTransaction]);

  const openUpdateAliasDialog = React.useCallback(() => {
    setIsUpdateAliasDialogOpen(true);
  }, []);

  const closeUpdateAliasDialog = React.useCallback(() => {
    if (!isAliasSubmitting) {
      setIsUpdateAliasDialogOpen(false);
    }
  }, [isAliasSubmitting]);

  const handleUpdateAlias = React.useCallback(
    async (payload) => {
      setIsAliasSubmitting(true);
      try {
        await updateCustomerAlias(payload);
        await loadLeftPanels({ preAutoLink: false });

        if (selectedTransaction?.id) {
          await loadTransactionDetails(selectedTransaction, {}, { autoLink: false });
        }

        setImportToast({
          open: true,
          severity: 'success',
          message: 'Alias updated with priority 100.',
        });
      } catch (err) {
        const message = err?.response?.data?.error || err?.message || 'Failed to update alias.';
        setImportToast({
          open: true,
          severity: 'error',
          message,
        });
      } finally {
        setIsAliasSubmitting(false);
      }
    },
    [loadLeftPanels, loadTransactionDetails, selectedTransaction]
  );

  const handleRemoveAlias = React.useCallback(
    async (payload) => {
      setIsAliasSubmitting(true);
      try {
        await removeCustomerAlias(payload);
        await loadLeftPanels({ preAutoLink: false });

        if (selectedTransaction?.id) {
          await loadTransactionDetails(selectedTransaction, {}, { autoLink: false });
        }

        setImportToast({
          open: true,
          severity: 'success',
          message: 'Alias removed.',
        });
      } catch (err) {
        const message = err?.response?.data?.error || err?.message || 'Failed to remove alias.';
        setImportToast({
          open: true,
          severity: 'error',
          message,
        });
      } finally {
        setIsAliasSubmitting(false);
      }
    },
    [loadLeftPanels, loadTransactionDetails, selectedTransaction]
  );

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      {/* ==================================================================== */}
      {/* BLOCK BP.PAGE.002.09 — Header and request status */}
      {/* ==================================================================== */}
      <Typography variant="h4" sx={{ mb: 2 }}>
        Bank Posting
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {(loading || submitting) && <CircularProgress size={24} sx={{ mb: 2 }} />}

      {/* ==================================================================== */}
      {/* BLOCK BP.PAGE.002.10 — Main three-column region */}
      {/* ==================================================================== */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '1.25fr 64px 0.95fr',
          },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {/* ================================================================== */}
        {/* BLOCK BP.PAGE.002.11 — Left transaction panel */}
        {/* ================================================================== */}
        <Box>
          <BankPostingSearchBox
            label="Local Search Box"
            value={filters.search}
            onChange={(value) => setFilterValue('search', value)}
            placeholder="Text, invoice list, amount range, or green/yellow/blank"
            helperText="Spaces = AND, commas = OR. Examples: 0442587 0445511 | 0442587,0445511 | green | yellow | blank | balance:450to800"
          />

          <Box sx={{ mt: 1 }}>
            <BankPostingTransactionTree
              title="Bank Transactions"
              groups={filteredTransactionGroups}
              selectedId={selectedTransaction?.id}
              onSelect={handleSelectTransaction}
              checkedTransactionIds={checkedTransactionIds}
              onToggleTransaction={handleToggleTransaction}
              assignedInvoicesByTransactionId={assignedInvoicesByTransactionId}
              checkedChildKeys={checkedChildKeys}
              onToggleChildInvoice={handleToggleChildInvoice}
              onUpdateAssignedInvoice={handleUpdateAssignedInvoice}
            />
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
            <TextField
              label="Deposit Comment Override (Optional)"
              size="small"
              value={submitForm.depositComment}
              onChange={(event) => setSubmitValue('depositComment', event.target.value)}
              sx={{ ...selectableTextFieldSx, minWidth: { xs: '100%', sm: 320 } }}
            />

            <Button
              variant="outlined"
              onClick={handleRefreshTransactionList}
              disabled={loading || submitting}
            >
              Refresh List
            </Button>
          </Stack>

          {visibleZeroBalanceCount > 0 && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={zeroBalanceAutoSelectEnabled}
                  onChange={handleToggleZeroBalanceAutoSelect}
                />
              }
              label={`Select $0 Balance (${visibleZeroBalanceCount})`}
            />
          )}

          {hasCheckedTransactions && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'info.main' }}>
              Bank Code auto-derived per checked row:{' '}
              {checkedDerivedBankCodes.length
                ? checkedDerivedBankCodes.join(', ')
                : 'Unknown (account must end in 3890 or 3882)'}
            </Typography>
          )}

          <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleRunSeedRebuild}
              disabled={loading || submitting}
            >
              Rebuild Match Seeds
            </Button>

            <Button
              variant="outlined"
              onClick={handleRunAutoMatchWithProgress}
              disabled={loading || submitting}
            >
              Run Auto-Match
            </Button>

            <Button
              variant="outlined"
              color="warning"
              onClick={() => {
                if (
                  window.confirm(
                    'Start Over will clear current invoice assignments and open-link rows before rerunning auto-match. Continue?'
                  )
                ) {
                  handleStartOverWithProgress();
                }
              }}
              disabled={loading || submitting}
            >
              Start Over
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                if (
                  window.confirm(
                    'This will re-mark submitted transactions as cleared/removed. Use after a Start Over accidentally brought back posted transactions. Continue?'
                  )
                ) {
                  handleRestoreCleared();
                }
              }}
              disabled={loading || submitting}
            >
              Restore Cleared State
            </Button>

            <Button
              variant="outlined"
              onClick={openUpdateAliasDialog}
              disabled={!selectedTransaction || loading || submitting}
            >
              Update Alias
            </Button>

            <Button
              variant="contained"
              onClick={() => handleMarkRemoved(checkedTransactions)}
              disabled={!hasCheckedTransactions}
            >
              remove Selected
            </Button>

            <Button
              variant="contained"
              onClick={() => handleSubmitSelected(checkedTransactions)}
              disabled={submitButtonDisabled}
            >
              submit Selected
            </Button>
          </Stack>

          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'info.main' }}>
            Placement target: {placementTargetLabel}
          </Typography>

          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
            Auto-match now runs only from this button. Queue, paging, filters, and normal refreshes
            stay read-only.
          </Typography>

          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
            Start Over clears invoice/open-link assignments, then rebuilds auto matches from the
            backend without deleting BankTransactionState rows.
          </Typography>

          {hasCheckedTransactions && !allCheckedRowsZeroBalance && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.main' }}>
              Submit blocked: {nonSubmittableCheckedCount} checked row(s) are not submittable
              (non-zero balance or missing invoice number).
            </Typography>
          )}
        </Box>

        {/* ================================================================== */}
        {/* BLOCK BP.PAGE.002.12 — Center action lane */}
        {/* ----------------------------------------------------------------------------
            BUTTONS
            - Top left arrow: Suggested invoices -> selected transaction
            - Middle left arrow: Open invoices -> selected transaction
            - Right arrow: assigned child invoices -> back out of selected transaction
           ================================================================== */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: { xs: 0, lg: 9 },
            gap: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={moveSuggestedToSelected}
            disabled={!checkedSuggested.length || !selectedTransaction}
            sx={{
              minWidth: 44,
              width: 44,
              height: 44,
              p: 0,
              fontSize: '1.35rem',
              lineHeight: 1,
            }}
            title="Assign selected suggested invoices"
          >
            ←
          </Button>

          <Button
            variant="outlined"
            onClick={moveOpenToSelected}
            disabled={!checkedOpen.length || !selectedTransaction}
            sx={{
              minWidth: 44,
              width: 44,
              height: 44,
              p: 0,
              fontSize: '1.35rem',
              lineHeight: 1,
            }}
            title="Assign selected open invoices"
          >
            ←
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            onClick={moveSelectedBackToInvoicePool}
            disabled={!checkedChildKeys.length}
            sx={{
              minWidth: 44,
              width: 44,
              height: 44,
              p: 0,
              fontSize: '1.35rem',
              lineHeight: 1,
            }}
            title="Move selected assigned invoices back from bank transaction"
          >
            →
          </Button>
        </Box>

        {/* ================================================================== */}
        {/* BLOCK BP.PAGE.002.13 — Right suggested and open panels */}
        {/* ================================================================== */}
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'info.main' }}>
              Total Selected (Suggested + Open): {formatMoney(totalSelectedToPost)}
            </Typography>

            <Typography
              variant="caption"
              sx={{ display: 'block', mb: 0.75, color: 'text.secondary' }}
            >
              Suggested Selected: {formatMoney(suggestedSelectedTotal)}
            </Typography>

            <BankPostingSearchBox
              label="Local Search Box"
              value={filters.suggestedSearch}
              onChange={(value) => setFilterValue('suggestedSearch', value)}
              placeholder="Search invoice numbers only"
              helperText="Type any invoice fragment such as 399, 467399, or 0467399. Dates and amounts are not used in Suggested invoice search."
            />

            <Box sx={{ mt: 1 }}>
              <BankPostingInvoiceList
                title="Suggested invoices"
                rows={displaySuggested}
                selectedKeys={checkedSuggested}
                onToggle={toggleKey(setCheckedSuggested)}
                onAssignedElsewhereClick={handleAssignedElsewhereInvoiceClick}
                kind="suggested"
                minHeight={210}
                pagination={selectedDetails.suggestedPagination}
                onPageChange={handleChangeSuggestionPage}
                maxHeight={260}
              />
            </Box>
          </Box>

          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Open invoices
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    backgroundColor: '#d97a2b',
                    color: '#fff',
                    px: 1,
                    py: 0.375,
                    borderRadius: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  date range
                </Box>

                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => setFilterValue('fromDate', e.target.value)}
                  style={{ height: 30 }}
                />

                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => setFilterValue('toDate', e.target.value)}
                  style={{ height: 30 }}
                />
              </Stack>
            </Stack>

            <Typography
              variant="caption"
              sx={{ display: 'block', mb: 0.75, color: 'text.secondary' }}
            >
              {remainingToZeroLabel}
            </Typography>

            <BankPostingSearchBox
              label="Local Search Box"
              value={filters.openInvoiceSearch}
              onChange={(value) => setFilterValue('openInvoiceSearch', value)}
              placeholder="Search open invoices by invoice, range, amount, or text"
              helperText="Spaces = AND, commas = OR. Examples: 0442587 0445511 | 0442587,0445511 | 0442587to0442677 | balance:450 475 | balance:450,475 | balance:450to800"
            />

            {!!filters.openInvoiceSearch && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.5,
                  color: detailLoading ? 'info.main' : 'text.secondary',
                }}
              >
                {!selectedTransaction?.id
                  ? 'Select a bank transaction first to process open invoice search.'
                  : detailLoading
                    ? 'Processing open invoice search...'
                    : `Open invoice search complete. Results: ${displayOpen.length}`}
              </Typography>
            )}

            <Box sx={{ mt: 1 }}>
              <BankPostingInvoiceList
                title=""
                rows={displayOpen}
                selectedKeys={checkedOpen}
                onToggle={toggleKey(setCheckedOpen)}
                onAssignedElsewhereClick={handleAssignedElsewhereInvoiceClick}
                kind="open"
                minHeight={210}
                hideTitle
                pagination={selectedDetails.openPagination}
                onPageChange={handleChangeOpenPage}
                maxHeight={260}
              />
            </Box>
          </Box>
        </Stack>
      </Box>

      {/* ==================================================================== */}
      {/* BLOCK BP.PAGE.002.14 — Bottom row panels */}
      {/* ==================================================================== */}
      <Box
        sx={{
          mt: 2,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '1.25fr 64px 0.95fr',
          },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Box sx={{ gridColumn: { xs: '1', lg: '1' } }}>
          <BankPostingSearchBox
            label="Local Search Box"
            value={filters.removedSearch}
            onChange={(value) => setFilterValue('removedSearch', value)}
            placeholder="Search removed rows by text or amount"
            helperText="Spaces = AND. Examples: 665 | vendor name | balance:450to800"
          />

          <Box sx={{ mt: 1 }}>
            <BankPostingRemovedItems
              groups={filteredRemovedGroups}
              selectedRemovedStateIds={selectedRemovedStateIds}
              onRestore={handleRestoreRemoved}
              onToggleCleared={handleUpdateRemovedClearFlag}
              onToggleAllCleared={handleToggleAllRemovedClearFlags}
              onClearRemoved={handleClearRemovedItems}
            />
          </Box>

          <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
            <Button variant="contained" color="info" onClick={openImportDialog}>
              Import Bank CSV
            </Button>
          </Stack>
        </Box>

        <Box sx={{ gridColumn: { xs: '1', lg: '3' } }}>
          <BankPostingSearchBox
            label="Past Posting Search"
            value={filters.pastPostingSearch || ''}
            onChange={(value) => setFilterValue('pastPostingSearch', value)}
            placeholder="Search past postings by customer, deposit date, invoice, amount, total, check, or comment"
            helperText="Space-separated values use AND. Date examples: 1/1/2026, 01/01/26, 1-1-26. Combined filter examples: $470, 03/1/2026to03/31/2026 or amount:470 date:03/1/2026to03/31/2026 (amount: matches deposit total). Click a deposit row, then use Reverse Posting to stage a negative reversal."
          />

          <Box sx={{ mt: 1 }}>
            <BankPostingPastPostingsList
              rows={pastPostingRows}
              loading={pastPostingLoading}
              hasSearch={hasPastPostingSearch}
              selectedDepositKey={selectedPastPosting?.key || ''}
              onSelectDeposit={handleSelectPastPosting}
              minHeight={190}
              maxHeight={220}
            />
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ mt: 1.25 }}>
            <Button
              variant="contained"
              color="warning"
              onClick={openReverseConfirmDialog}
              disabled={!hasSelectedPastPosting || isSelectedPastPostingReversed || submitting}
            >
              Reverse Posting
            </Button>

            {hasSelectedPastPosting && (
              <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
                Selected: Deposit {selectedPastPosting?.depositNo || '-'} / Batch{' '}
                {selectedPastPosting?.batchNo || '-'}
                {isSelectedPastPostingReversed ? ' (REVERSED)' : ''}
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>

      <Dialog
        open={isReverseConfirmOpen}
        onClose={closeReverseConfirmDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Reverse Posting</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.25 }}>
            Do you want to reverse posting for the selected entry?
          </Typography>

          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Date: {selectedPastPosting?.date || '-'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Bank Code: {selectedPastPosting?.bankCode || '-'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Deposit No: {selectedPastPosting?.depositNo || '-'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Batch No: {selectedPastPosting?.batchNo || '-'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Status: {isSelectedPastPostingReversed ? 'REVERSED' : 'Not Reversed'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Deposit Total: {formatMoney(selectedPastPosting?.depositTotal || 0)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comment: {selectedPastPosting?.depositComment || '-'}
          </Typography>
        </DialogContent>
        <DialogActions>
          {isSelectedPastPostingReversed ? (
            <Button onClick={closeReverseConfirmDialog} disabled={submitting}>
              Close
            </Button>
          ) : (
            <>
              <Button onClick={closeReverseConfirmDialog} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={confirmReversePastPosting}
                disabled={submitting}
              >
                {submitting ? 'Reversing...' : 'Confirm Reverse'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={isImportDialogOpen} onClose={closeImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Import Bank CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.25 }}>
            Select a local bank CSV file. Import inserts new transactions, skips duplicates by
            transaction id already in the list, preserves same-file same-date duplicates as new
            transactions, and ignores rows where Post Date includes a time.
          </Typography>

          <input
            ref={csvFileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleSelectedImportFile}
            style={{ display: 'none' }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="center">
            <Button variant="outlined" onClick={triggerFilePicker} disabled={csvImporting}>
              Choose CSV File
            </Button>

            <Typography variant="body2" color="text.secondary">
              {selectedImportFile?.name || 'No file selected'}
            </Typography>
          </Stack>

          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={importRebuildSeeds}
                  onChange={(event) => setImportRebuildSeeds(event.target.checked)}
                  disabled={csvImporting}
                />
              }
              label="Rebuild match seeds after import"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={importRunAutoMatch}
                  onChange={(event) => setImportRunAutoMatch(event.target.checked)}
                  disabled={csvImporting}
                />
              }
              label="Run auto-match after import"
            />
          </Stack>

          {csvImportResult && (
            <Alert severity="info" sx={{ mt: 1.5 }} onClose={clearCsvImportResult}>
              {[buildImportSummary(csvImportResult), buildImportPostActionSummary(csvImportResult)]
                .filter(Boolean)
                .join(' | ')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImportDialog} disabled={csvImporting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRunCsvImport}
            disabled={csvImporting || !selectedImportFile}
          >
            {csvImporting ? 'Importing...' : 'Submit Import'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={importToast.open}
        autoHideDuration={6000}
        onClose={handleCloseImportToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseImportToast}
          severity={importToast.severity}
          sx={{ width: '100%' }}
        >
          {importToast.message}
        </Alert>
      </Snackbar>

      <Dialog open={autoMatchProgressOpen} maxWidth="xs" fullWidth>
        <DialogTitle>Auto Match Running</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} alignItems="flex-start" sx={{ py: 0.5 }}>
            <CircularProgress size={24} />
            <Typography variant="body2">Total items: {autoMatchProgressTotal}</Typography>
            {autoMatchProgressRemaining > 0 ? (
              <Typography variant="body2" color="text.secondary">
                Estimated remaining: {autoMatchProgressRemaining}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              Estimated duration: {autoMatchEstimatedDurationSeconds}s | Elapsed:{' '}
              {Math.round(autoMatchElapsedSeconds)}s
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>

      <BankPostingUpdateAliasDialog
        open={isUpdateAliasDialogOpen}
        onClose={closeUpdateAliasDialog}
        onUpdate={handleUpdateAlias}
        onRemove={handleRemoveAlias}
        onLoadAliases={fetchCustomerAliases}
        defaultAliasText={updateAliasDefaults.aliasText}
        defaultCustomerNo={updateAliasDefaults.customerNo}
        defaultArDivisionNo={updateAliasDefaults.arDivisionNo}
        defaultCustomerName={updateAliasDefaults.customerName}
        submitting={isAliasSubmitting}
      />
    </Box>
  );
}
